import { useState } from "react";
import { useAttemptLeave } from "./unsavedGuard";
import { CHANNELS, ConfigPrompt, type Channel } from "./ConfigPrompt";
import { ConfigModelo } from "./ConfigModelo";
import { ConfigTools } from "./ConfigTools";
import { ConfigEstoque } from "./ConfigEstoque";
import { ConfigAvancadas } from "./ConfigAvancadas";
import { ConfigFollowup } from "./ConfigFollowup";
import { ConfigVoz } from "./ConfigVoz";
import { ConfigPortais } from "./ConfigPortais";
import { ConfigCRMillion } from "./ConfigCRMillion";
import { PortalLayout } from "./builder/PortalLayout";
import { CamposTab } from "./builder/CamposTab";

const SUB_TABS = [
  { id: "prompt", label: "Prompt" },
  { id: "campos", label: "Campos" },  // só global: campos editáveis (coleção própria, chips em qualquer sessão)
  { id: "ordem", label: "Ordem" },  // só global: layout do portal do cliente (blocos → campos)
  { id: "modelo", label: "Modelo" },
  { id: "tools", label: "Tools" },
  { id: "estoque", label: "Tool Estoque" },
  { id: "avancadas", label: "Avançadas" },
  { id: "followup", label: "Followup" },
  { id: "voz", label: "Voz" },
  { id: "portais", label: "Portais" },
  { id: "crmillion", label: "CRMillion" },
] as const;

type SubTabId = (typeof SUB_TABS)[number]["id"];

// Abas com perfil por canal: cada canal tem o seu (prompt, tools, follow-up).
const CHANNEL_TABS: ReadonlySet<SubTabId> = new Set<SubTabId>(["prompt", "tools", "followup"]);

export function TabConfig({ agent }: { agent: any }) {
  const [activeTab, setActiveTab] = useState<SubTabId>("prompt");
  const [channel, setChannel] = useState<Channel>("whatsapp");
  const attemptLeave = useAttemptLeave();
  const simplified = !!agent.template_id;  // Self-Service: agente do portal do cliente; modelo vem do global
  const isGlobal = agent.template_kind === "global";
  // Self-Service é só WhatsApp (por enquanto): implantado e global sem sub-abas de canal.
  const showChannels = CHANNEL_TABS.has(activeTab) && !simplified && !isGlobal;
  const ch: Channel = showChannels ? channel : "whatsapp";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Sub-tab bar */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 20,
          flexWrap: "wrap",
          padding: "16px 24px 0",
          flexShrink: 0,
        }}
      >
        {SUB_TABS.filter((t) => !(simplified && t.id === "modelo") && !(!isGlobal && (t.id === "ordem" || t.id === "campos"))).map((t) => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              onClick={() => { if (t.id !== activeTab) attemptLeave(() => setActiveTab(t.id)); }}
              style={{
                fontSize: 13,
                padding: "6px 12px",
                borderRadius: 8,
                border: isActive ? "none" : "1px solid var(--line-strong)",
                background: isActive ? "var(--brand)" : "var(--surface-hover)",
                color: isActive ? "#fff" : "var(--ink-secondary)",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Sub-abas de canal (Prompt/Tools/Followup) — WhatsApp | Instagram | Messenger */}
      {showChannels && (
        <div role="tablist" style={{ display: "flex", gap: 4, margin: "-8px 24px 16px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
          {CHANNELS.map((c) => {
            const isActive = c.id === channel;
            return (
              <button
                key={c.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => { if (c.id !== channel) attemptLeave(() => setChannel(c.id)); }}
                style={{
                  fontSize: 13, padding: "6px 14px", background: "none", border: "none", cursor: "pointer",
                  borderBottom: `2px solid ${isActive ? "var(--brand)" : "transparent"}`,
                  color: isActive ? "var(--ink)" : "var(--ink-soft)", fontWeight: isActive ? 600 : 400, marginBottom: -1,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Sub-tab content — key={ch} remonta o editor ao trocar de canal (estado não vaza entre canais) */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px" }}>
        {activeTab === "prompt" && (simplified ? <PortalManagedNote agent={agent} /> : <ConfigPrompt key={ch} agent={agent} channel={ch} />)}
        {activeTab === "campos" && <CamposTab agent={agent} />}
        {activeTab === "ordem" && <PortalLayout agent={agent} />}
        {activeTab === "modelo" && <ConfigModelo agent={agent} />}
        {activeTab === "tools" && <ConfigTools key={ch} agent={agent} channel={ch} />}
        {activeTab === "estoque" && <ConfigEstoque agent={agent} />}
        {activeTab === "avancadas" && <ConfigAvancadas agent={agent} />}
        {activeTab === "followup" && <ConfigFollowup key={ch} agent={agent} channel={ch} />}
        {activeTab === "voz" && <ConfigVoz agent={agent} />}
        {activeTab === "portais" && <ConfigPortais agent={agent} />}
        {activeTab === "crmillion" && <ConfigCRMillion agent={agent} />}
      </div>
    </div>
  );
}

// Agente criado pelo cliente no portal (agentes.revendai.com): prompt, campos e
// tools vêm do Agente Global; o cliente preenche no portal. Aqui só aviso.
function PortalManagedNote({ agent }: { agent: any }) {
  return (
    <div style={{ fontSize: 13, color: "var(--ink-secondary)", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 16, lineHeight: 1.6, maxWidth: 720 }}>
      Este agente é configurado pelo cliente no portal. Prompt, campos e tools vêm do Agente Global{" "}
      <strong style={{ color: "var(--ink)" }}>{agent.global_form?.name ?? "global"}</strong>; edite o global no painel Dev.
    </div>
  );
}
